import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { createDevJwt } from "../utils/devJwt";
import ThemeModeToggle from "../components/layout/ThemeModeToggle";
import { useAuth } from "../contexts/AuthContext";
import { isSamiIamEnabled, setSamiIamDevOverride } from "../utils/samiIam";

interface DevLoginPageProps {
  onLogin: (token: string) => Promise<void>;
  error?: string | null;
}

const DevLoginPage: React.FC<DevLoginPageProps> = ({ onLogin, error }) => {
  const { startIamLogin } = useAuth();
  const [email, setEmail] = useState("dev.user@example.com");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [iamSwitchOn, setIamSwitchOn] = useState(() => isSamiIamEnabled());

  const handleIamSwitchChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSamiIamDevOverride(checked ? "on" : "off");
    setIamSwitchOn(checked);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setLocalError("Email is required.");
      return;
    }

    setSubmitting(true);
    try {
      const token = await createDevJwt({ email: trimmedEmail });
      await onLogin(token);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCognitoSignIn = () => {
    setLocalError(null);
    setSubmitting(true);
    startIamLogin();
  };

  const displayError = localError ?? error;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        bgcolor: "background.default",
        py: 4,
        position: "relative",
      }}
    >
      <Box sx={{ position: "absolute", top: 16, right: 16 }}>
        <ThemeModeToggle />
      </Box>
      <Container maxWidth="sm">
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Development Login
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Local dev mode — sign in with your email, or use SAMI IAM (Cognito)
              when enabled below.
            </Typography>

            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <Switch
                  checked={iamSwitchOn}
                  onChange={handleIamSwitchChange}
                />
              }
              label={`Use SAMI IAM (Cognito) — ${iamSwitchOn ? "on" : "off"}`}
            />

            {displayError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {displayError}
              </Alert>
            )}

            {iamSwitchOn ? (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Cognito Hosted UI handles sign-in. You will be redirected back
                  to this app after authentication.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  onClick={() => void handleCognitoSignIn()}
                >
                  {submitting ? "Redirecting…" : "Sign in with Cognito"}
                </Button>
              </Stack>
            ) : (
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                    autoComplete="email"
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={submitting}
                  >
                    {submitting ? "Signing in…" : "Sign in"}
                  </Button>
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default DevLoginPage;
